import { Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, IZalozeniLeiInput } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import checkIcApi from 'services/lei/checkIC';
import leiRejstrikApi from 'services/lei/leiRejstrik';
import zalozeniLeiApi from 'services/lei/zalozeniLei';

import { ETypZadostiLei } from 'enums/lei/leiDataEnums';
import routePathEnum from 'enums/routePathEnum';

import anoNe from 'data/codebooks/anoNeSelect';
import {
    leiCountrySelect,
    leiCountrySelectFondy,
    leiCountrySelectInternational
} from 'data/codebooks/lei/leiCountrySelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';
import leiRejstrikValidation from 'utils/validations/leiRejstrikValidation';

interface IPruvodce {
    prevStep: () => void;
    nextStep: () => void;
    pruvodceData?: IZalozeniLeiInput;
    formDataSpolecnost?: ILeiData['spolecnost'];
    typZadosti?: ETypZadostiLei;
    setPruvodceData: React.Dispatch<React.SetStateAction<IZalozeniLeiInput | undefined>>;
    setFormData: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFinishOuter?: (fieldsValue: any) => void;
    icoErrorOuter?: string;
}

const Pruvodce: React.FC<IPruvodce> = ({
    prevStep,
    nextStep,
    pruvodceData,
    formDataSpolecnost,
    typZadosti,
    setPruvodceData,
    setFormData,
    onFinishOuter,
    icoErrorOuter
}) => {
    const [form] = Form.useForm();
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { lei, language, aktivniFirma } = useContext(Context);
    const [showDefaultValues, setShowDefaultValues] = useState<{ region: boolean; registrationauthorityid: boolean }>({
        region: true,
        registrationauthorityid: true
    });
    const [icoError, setIcoError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [rerenderValidate, setRerenderValidate] = useState<boolean>(false);
    const setForceRerender = useState<boolean>(false)[1];
    const rerender = (validateAfter?: boolean) => {
        if (validateAfter) {
            setRerenderValidate(true);
        } else {
            setForceRerender(val => !val);
        }
    };

    useEffect(() => {
        if (rerenderValidate) {
            form.validateFields(['registrationauthorityentityid']);
            setRerenderValidate(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerenderValidate]);

    useEffect(() => {
        form.setFieldsValue({
            maid:
                pruvodceData?.registrationauthorityentityid || formDataSpolecnost?.registrationauthorityentityid
                    ? 'ano'
                    : formDataSpolecnost?.legalname
                      ? 'ne'
                      : undefined
        });
        setTimeout(() => rerender(), 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    console.log(pruvodceData, formDataSpolecnost);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        setLoading(true);
        setPruvodceData(fieldsValue);
        const zalozeniData = {
            ...(fieldsValue.jurisdikce && {
                jurisdikce: fieldsValue.jurisdikce
            }),
            ...(fieldsValue.registrationauthorityid && {
                registrationauthorityid: fieldsValue.registrationauthorityid
            }),
            ...(fieldsValue.otherregistrationauthorityid && {
                otherregistrationauthorityid: fieldsValue.otherregistrationauthorityid
            }),
            ...(fieldsValue.registrationauthorityentityid && {
                registrationauthorityentityid: fieldsValue.registrationauthorityentityid
            }),
            ...(fieldsValue.legalname && {
                legalname: fieldsValue.legalname
            }),
            typzadosti: typZadosti
        };
        const finish = () => {
            const formDataSet = () =>
                setFormData(pv => {
                    const pvSpolecnost: ILeiData['spolecnost'] = {
                        ...pv?.spolecnost
                    };
                    pvSpolecnost?.otherregistrationauthorityid && delete pvSpolecnost.otherregistrationauthorityid;
                    return {
                        ...pv,
                        entitycheck: 'N/A',
                        language: language,
                        spolecnost: {
                            ...pvSpolecnost,
                            ...(fieldsValue.legalname && {
                                legalname: fieldsValue.legalname
                            }),
                            ...(fieldsValue.registrationauthorityid && {
                                registrationauthorityid: fieldsValue.registrationauthorityid
                            }),
                            ...(fieldsValue.otherregistrationauthorityid &&
                                fieldsValue.registrationauthorityid === 'RA888888' && {
                                    otherregistrationauthorityid: fieldsValue.otherregistrationauthorityid
                                }),
                            jurisdikce: fieldsValue.jurisdikce,
                            registrationauthorityentityid: fieldsValue.registrationauthorityentityid,
                            ...(typZadosti !== ETypZadostiLei.INTERNATIONAL_ORGANIZATION && {
                                adresy: [
                                    {
                                        typadresy: 'LEGALADDRESS',
                                        country: fieldsValue.jurisdikce
                                        // region: fieldsValue.region
                                    }
                                ]
                            })
                        }
                    };
                });
            const promises = [];
            if ((fieldsValue.jurisdikce === 'CZ' || fieldsValue.jurisdikce === 'SK') && fieldsValue.maid !== 'ne') {
                promises.push(
                    leiRejstrikApi({
                        stat: fieldsValue.jurisdikce,
                        lang: 'cz',
                        ic: fieldsValue.registrationauthorityentityid,
                        regauthor: fieldsValue.registrationauthorityid
                    })
                        .then(respData =>
                            setFormData(pv => {
                                const dataResp = {
                                    ...respData,
                                    ...(respData.entitycreationdate && {
                                        entitycreationddate: respData.entitycreationdate
                                    })
                                };
                                delete dataResp.entitycheck;
                                const pvSpolecnost: ILeiData['spolecnost'] = {
                                    ...pv?.spolecnost
                                };
                                pvSpolecnost?.otherregistrationauthorityid &&
                                    delete pvSpolecnost.otherregistrationauthorityid;
                                return {
                                    ...pv,
                                    entitycheck: respData.entitycheck || 'N/A',
                                    language: language,
                                    spolecnost: {
                                        ...pvSpolecnost,
                                        ...dataResp,
                                        jurisdikce: dataResp.jurisdikce || fieldsValue.jurisdikce,
                                        ...(fieldsValue.registrationauthorityid && {
                                            registrationauthorityid: fieldsValue.registrationauthorityid
                                        }),
                                        ...(fieldsValue.otherregistrationauthorityid &&
                                            fieldsValue.registrationauthorityid === 'RA888888' && {
                                                otherregistrationauthorityid: fieldsValue.otherregistrationauthorityid
                                            }),
                                        adresy: [
                                            {
                                                typadresy: 'LEGALADDRESS',
                                                ...(pv?.spolecnost?.adresy && pv.spolecnost.adresy.length
                                                    ? {
                                                          country: pv.spolecnost.adresy[0].country
                                                      }
                                                    : {}),
                                                ...respData.adresy[0]
                                            }
                                        ]
                                    }
                                };
                            })
                        )
                        .catch(() => formDataSet())
                );
            }

            !lei?.idVarsymZadosti?.id &&
                promises.push(
                    zalozeniLeiApi(zalozeniData).then(resp => {
                        setLevel('lei', {
                            ...lei,
                            selectedLei: {
                                varsymbol: resp.data.varsym
                            },
                            idVarsymZadosti: resp.data
                        });
                        if (fieldsValue.jurisdikce !== 'CZ' && fieldsValue.jurisdikce !== 'SK') {
                            formDataSet();
                        }
                    })
                );
            Promise.all(promises).then(() => {
                promises.length === 0 && formDataSet();
                nextStep();
            });
        };
        /* typZadosti !== ETypZadostiLei.FUND && */ fieldsValue.registrationauthorityentityid
            ? checkIcApi(zalozeniData).then(data => {
                  if (
                      (data.stav !== 'EXISTUJE' && data.stav !== 'DUPLICITA') ||
                      fieldsValue.registrationauthorityentityid === formDataSpolecnost?.registrationauthorityentityid
                  ) {
                      finish();
                  } else {
                      data.varsym
                          ? (setLevel('lei', {
                                selectedLei: lei?.selectedLei,
                                idVarsymZadosti: {
                                    varsym: data.varsym
                                }
                            }),
                            navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.LEI_DETAIL}`))
                          : setIcoError(
                                data.stav === 'EXISTUJE'
                                    ? data.stavlei === 'ROZP'
                                        ? 'LEI_ICO_CHECK_ERR_JINY'
                                        : data.lou
                                          ? data.lou
                                          : 'LEI_ICO_CHECK_ERR'
                                    : 'LEI_ICO_CHECK_ERR_DUPL'
                            );
                      setLoading(false);
                  }
              })
            : finish();
    };

    return (
        <LoadingWrapper loading={loading} loadingText="NACITANI_DAT">
            {typZadosti === ETypZadostiLei.FUND && (
                <Paragraph
                    text="LEI_FUND_TEXT1"
                    style={{
                        // marginTop: 12,
                        display: 'block',
                        marginBottom: 10
                    }}
                />
            )}
            {typZadosti === ETypZadostiLei.FUND && (
                <Paragraph
                    text="LEI_FUND_TEXT2"
                    style={{
                        // marginTop: 12,
                        display: 'block',
                        marginBottom: 10
                    }}
                />
            )}
            {typZadosti === ETypZadostiLei.BRANCH && (
                <Paragraph
                    text={'LEI_BRANCH_TEXT2'}
                    style={{
                        marginBottom: 12,
                        display: 'block'
                    }}
                />
            )}
            <Title level={5} text="LEI_OSOBA_TITLE" />
            <FormWrapper
                outForm={form}
                onFinish={() =>
                    onFinishOuter ? onFinishOuter(form.getFieldsValue()) : onFinish(form.getFieldsValue())
                }
            >
                <SelectComponent
                    isFormItem={true}
                    name="jurisdikce"
                    defaultValue={pruvodceData?.jurisdikce || formDataSpolecnost?.jurisdikce}
                    labelParagraph="LEI_DETAIL_JURISDICKE"
                    data={
                        typZadosti === ETypZadostiLei.FUND
                            ? leiCountrySelectFondy
                            : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                              ? leiCountrySelectInternational
                              : leiCountrySelect
                    }
                    onChange={() => {
                        setShowDefaultValues(pv => {
                            return {
                                ...pv,
                                registrationauthorityid: false,
                                region: false
                            };
                        });
                        setIcoError('');
                        form.setFieldsValue({
                            maid: undefined,
                            region: undefined,
                            registrationauthorityid: undefined
                        });
                    }}
                />
                <SelectComponent
                    isFormItem={true}
                    name="registrationauthorityid"
                    defaultValue={
                        showDefaultValues.registrationauthorityid
                            ? pruvodceData?.registrationauthorityid || formDataSpolecnost?.registrationauthorityid
                            : undefined
                    }
                    labelParagraph="REJSTRIK_KOD"
                    showOptionValue={true}
                    optionValueFilter={['RA888888']}
                    data={[
                        ...(form.getFieldValue('jurisdikce')
                            ? _getKeyValue_(form.getFieldValue('jurisdikce'))(leiRejstrikySelect)
                            : []),
                        ...leiRejstrikyPermanent
                    ]}
                    onChange={val => {
                        val === 'RA000161' &&
                            form.setFieldsValue({
                                registrationauthorityentityid: 'ΗΕ'
                            });
                        rerender(true);
                    }}
                />
                {form.getFieldValue('registrationauthorityid') === 'RA888888' && (
                    <InputComponent
                        label="REJSTRIK_NAZEV"
                        name="otherregistrationauthorityid"
                        initialValue={
                            pruvodceData?.otherregistrationauthorityid ||
                            formDataSpolecnost?.otherregistrationauthorityid
                        }
                        maxLength={100}
                    />
                )}

                {typZadosti === ETypZadostiLei.FUND && (
                    <SelectComponent
                        name="maid"
                        labelParagraph="LEI_MA_FOND_ID"
                        isFormItem={true}
                        data={anoNe}
                        defaultValue={
                            pruvodceData?.registrationauthorityentityid ||
                            formDataSpolecnost?.registrationauthorityentityid
                                ? 'ano'
                                : formDataSpolecnost?.legalname
                                  ? 'ne'
                                  : undefined
                        }
                        onChange={() => rerender()}
                    />
                )}
                {form.getFieldValue('maid') === 'ne' && (
                    <InputComponent
                        label={'LEI_DETAIL_PRAV_SPOL'}
                        name="legalname"
                        initialValue={formDataSpolecnost?.legalname}
                        maxLength={100}
                    />
                )}
                {(form.getFieldValue('maid') === 'ano' || typZadosti !== ETypZadostiLei.FUND) && (
                    <InputComponent
                        label={typZadosti === ETypZadostiLei.FUND ? 'LEI_FOND_ID' : 'LEI_ICO'}
                        name="registrationauthorityentityid"
                        initialValue={
                            pruvodceData?.registrationauthorityentityid ||
                            formDataSpolecnost?.registrationauthorityentityid
                        }
                        notRequired={typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION}
                        rules={leiRejstrikValidation(
                            (form.getFieldValue('registrationauthorityid') === 'RA888888' &&
                                form.getFieldValue('jurisdikce') === 'CZ' &&
                                'RA000164') ||
                                form.getFieldValue('registrationauthorityid')
                        )}
                        extramessage={
                            (icoError || icoErrorOuter)?.includes('LEI') ? (
                                icoError || icoErrorOuter
                            ) : icoError || icoErrorOuter ? (
                                <Paragraph
                                    text="LEI_ICO_CHECK_ERR_JINELOU"
                                    translateParams={[icoError || icoErrorOuter || '']}
                                    className="extramessage-text"
                                />
                            ) : (
                                ''
                            )
                        }
                        maxLength={30}
                        onChange={() => {
                            setIcoError('');

                            /* form.setFieldsValue({
                                registrationauthorityentityid: e.target.value.split(' ').join('')
                            }); */
                        }}
                    />
                )}
                <Row className="button-row">
                    {!formDataSpolecnost && (
                        <ButtonComponent
                            id="pruvodceZpetBtn"
                            type="default"
                            onClick={() => prevStep()}
                            buttontext="ZPET"
                        />
                    )}
                    <ButtonComponent
                        buttontext="POKRACOVAT"
                        type="primary"
                        onClick={() => form.submit()}
                        id="pruvodceSubmitBtn"
                    />
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default Pruvodce;
